import { useEffect, useState } from "react";

const FLAG_KEY = "search-suggestions";

export interface SuggestionsFlag {
  /** False on SSR and first client render — callers must treat it as off. */
  loaded: boolean;
  enabled: boolean;
}

/**
 * PostHog `search-suggestions` flag for staged rollout (kill switch without
 * redeploy). Unknown/absent flag systems resolve to enabled AFTER load so the
 * static env gates remain the hard switch; an explicit `false` disables.
 * Everything is resolved client-side — SSR/first render is always off, so
 * there is never a hydration mismatch.
 */
export function useSuggestionsFeatureFlag(): SuggestionsFlag {
  const [state, setState] = useState<SuggestionsFlag>({
    loaded: false,
    enabled: false,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { default: posthog } = await import("posthog-js");
        if (
          !cancelled &&
          typeof posthog?.isFeatureEnabled === "function" &&
          typeof posthog?.onFeatureFlags === "function"
        ) {
          try {
            const flagModule = (
              posthog as unknown as {
                featureFlags?: { getFlags?: () => string[] };
              }
            ).featureFlags;
            const flags = flagModule?.getFlags?.() ?? [];
            if (!flags || flags.length === 0) {
              await new Promise<void>((resolve) => {
                let done = false;
                const finish = () => {
                  if (!done) {
                    done = true;
                    resolve();
                  }
                };
                posthog.onFeatureFlags(finish);
                setTimeout(finish, 3000);
              });
            }
          } catch {
            // Flag fetch failed — fall through to a one-shot read.
          }
          if (!cancelled) {
            const value = posthog.isFeatureEnabled(FLAG_KEY);
            setState({ loaded: true, enabled: value !== false });
            return;
          }
        }
        if (!cancelled) setState({ loaded: true, enabled: true });
      } catch {
        if (!cancelled) setState({ loaded: true, enabled: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
